import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../api/client'
import logoIcon from '../assets/logo-icon.png'

// ── Design tokens (tangentmark.com language) ────────
const T = {
  paper: '#FBF8F1',
  ink: '#141414',
  accent: '#C1751B',
  accentDark: '#A85E12',
  muted: '#6B655D',
  hairline: 'rgba(0,0,0,0.06)',
  hairlineLight: 'rgba(0,0,0,0.03)',
}

// ── Helpers ──────────────────────────────────────────
const PHONE_RE = /^1[3-9]\d{9}$/
const isValidPhone = (v) => PHONE_RE.test(v)

// ── Shared input style ──────────────────────────────
const inputCls =
  'w-full px-4 py-3 rounded-xl border text-sm bg-white transition-all duration-200 focus:outline-none focus:ring-2 placeholder:text-[#8E8880]'

function inputStyle(focus = false) {
  return {
    borderColor: focus ? T.accent : T.hairline,
    boxShadow: focus ? `0 0 0 3px ${T.accent}22` : 'none',
  }
}

// ── Password eye toggle ─────────────────────────────
function PasswordInput({ value, onChange, placeholder, required, minLength, autoComplete }) {
  const [show, setShow] = useState(false)
  const [focused, setFocused] = useState(false)
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        autoComplete={autoComplete}
        className={inputCls}
        style={inputStyle(focused)}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E8880] hover:text-[#6B655D] transition-colors"
      >
        {show ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        )}
      </button>
    </div>
  )
}

// ── SMS countdown button ────────────────────────────
function SmsButton({ phone, disabled, onError }) {
  const [countdown, setCountdown] = useState(0)
  const [sending, setSending] = useState(false)
  const timerRef = useRef(null)

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current) }, [])

  const handleSend = async () => {
    if (!isValidPhone(phone) || countdown > 0 || sending) return
    setSending(true)
    try {
      await api.sendSmsCode(phone)
      setCountdown(60)
      timerRef.current = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) { clearInterval(timerRef.current); timerRef.current = null; return 0 }
          return c - 1
        })
      }, 1000)
    } catch (err) {
      // 以前这里是空的 `catch { /* parent handles */ }`：父级其实什么都没接，
      // 验证码发不出去时按钮只是恢复可点，用户完全不知道发生了什么
      onError?.(err?.message || err?.detail || '验证码发送失败，请稍后重试')
    } finally {
      setSending(false)
    }
  }

  const isDisabled = disabled || !isValidPhone(phone) || countdown > 0 || sending

  return (
    <button
      type="button"
      onClick={handleSend}
      disabled={isDisabled}
      className="shrink-0 px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-200 whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed"
      style={{
        borderColor: isDisabled ? T.hairline : T.accent,
        color: isDisabled ? T.muted : T.accent,
        background: 'transparent',
      }}
    >
      {sending ? '发送中...' : countdown > 0 ? `${countdown}s` : '获取验证码'}
    </button>
  )
}

// ── Spinner ─────────────────────────────────────────
function Spinner({ text }) {
  return (
    <span className="flex items-center justify-center gap-2">
      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      {text || '处理中...'}
    </span>
  )
}

// ── Tab bar ─────────────────────────────────────────
function TabBar({ tabs, active, onChange }) {
  return (
    <div className="flex gap-1 mb-6 p-1 rounded-xl" style={{ background: T.hairlineLight }}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className="flex-1 py-2 text-[13px] font-medium rounded-lg transition-all duration-200"
          style={{
            background: active === t.key ? '#fff' : 'transparent',
            color: active === t.key ? T.ink : T.muted,
            boxShadow: active === t.key ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

// ── Primary button ──────────────────────────────────
function PrimaryButton({ children, loading, disabled, type = 'submit' }) {
  return (
    <button
      type={type}
      disabled={loading || disabled}
      className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-[1px]"
      style={{
        background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`,
        boxShadow: `0 4px 16px ${T.accent}33`,
      }}
    >
      {loading ? <Spinner text="处理中..." /> : children}
    </button>
  )
}

// ══════════════════════════════════════════════════════
//  Main Component
// ══════════════════════════════════════════════════════
export default function LoginModal({ onClose }) {
  const [mode, setMode] = useState('login')        // login | register | forgot
  const [loginTab, setLoginTab] = useState('phone') // phone | email | wechat
  const [phoneLoginMode, setPhoneLoginMode] = useState('password') // password | sms
  const [regTab, setRegTab] = useState('phone')     // phone | email

  const [phone, setPhone] = useState('')
  const [smsCode, setSmsCode] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [name, setName] = useState('')

  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const [wechatQrUrl, setWechatQrUrl] = useState(null)
  const [wechatLoading, setWechatLoading] = useState(false)
  const [wechatError, setWechatError] = useState(false)

  const { login, register, phoneLogin, loginWithToken, verifyTwoFactor } = useAuth()
  const clearError = useCallback(() => setError(''), [])
  const errMsg = (err) => {
    if (!err) return '操作失败'
    if (typeof err === 'string') return err
    if (err.message && typeof err.message === 'string') return err.message
    if (err.detail && Array.isArray(err.detail) && err.detail.length > 0) {
      return err.detail.map(e => e.msg || e.message || '').filter(Boolean).join('; ') || '参数校验失败'
    }
    if (err.detail && typeof err.detail === 'string') return err.detail
    return '操作失败，请稍后重试'
  }
  const switchMode = (m) => { setMode(m); clearError() }

  // ── 2FA 续验：login 只给 5 分钟受限临时令牌，必须再走一步才有会话 ──
  const [pending2FA, setPending2FA] = useState(null)   // {tempToken, identifier}
  const [totp, setTotp] = useState('')

  // 返回 true 表示已转入 2FA 步骤，调用方就别关弹窗了 ——
  // 关掉等于把用户扔回落地页，再也没有输入验证码的入口。
  const beginTwoFactor = (result, identifier) => {
    if (!result || !result.two_factor_required || !result.temp_token) return false
    setPending2FA({ tempToken: result.temp_token, identifier })
    setTotp('')
    return true
  }

  const cancel2FA = () => { setPending2FA(null); setTotp(''); clearError() }

  const handleVerify2FA = async (e) => {
    e.preventDefault()
    if (!pending2FA) return
    if (!totp || totp.length < 6) return setError('请输入 6 位动态码或备用码')
    setError(''); setLoading(true)
    try { await verifyTwoFactor(pending2FA.tempToken, pending2FA.identifier, totp); onClose() }
    catch (err) { setError(errMsg(err)) }
    finally { setLoading(false) }
  }

  useEffect(() => {
    if (mode === 'login' && loginTab === 'wechat' && !wechatQrUrl && !wechatLoading) {
      setWechatLoading(true)
      api.getWechatQr()
        .then((d) => setWechatQrUrl(d.qr_url || d.qrUrl || null))
        .catch(() => setWechatError(true))
        .finally(() => setWechatLoading(false))
    }
  }, [mode, loginTab, wechatQrUrl, wechatLoading])

  // ── Phone + Password login ──
  const handlePhonePasswordLogin = async (e) => {
    e.preventDefault()
    if (!isValidPhone(phone)) return setError('请输入正确的手机号码')
    if (password.length < 6) return setError('密码至少 6 个字符')
    setError(''); setLoading(true)
    try {
      const res = await login(phone, password)
      if (!beginTwoFactor(res, phone)) onClose()
    }
    catch (err) { setError(errMsg(err)) }
    finally { setLoading(false) }
  }

  // ── Phone + SMS code login ──
  const handlePhoneSmsLogin = async (e) => {
    e.preventDefault()
    if (!isValidPhone(phone)) return setError('请输入正确的手机号码')
    if (!smsCode || smsCode.length < 4) return setError('请输入验证码')
    setError(''); setLoading(true)
    try { await phoneLogin(phone, smsCode); onClose() }
    catch (err) { setError(errMsg(err)) }
    finally { setLoading(false) }
  }

  const handleEmailLogin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true)
    try {
      const res = await login(email, password)
      if (!beginTwoFactor(res, email)) onClose()
    }
    catch (err) { setError(errMsg(err)) }
    finally { setLoading(false) }
  }

  const handlePhoneReg = async (e) => {
    if (!agreed) return setError('请先阅读并同意《服务协议》与《隐私政策》')
    e.preventDefault()
    if (!isValidPhone(phone)) return setError('请输入正确的手机号码')
    if (!smsCode || smsCode.length < 4) return setError('请输入验证码')
    if (password.length < 6) return setError('密码至少 6 个字符')
    if (!name.trim()) return setError('请输入姓名')
    setError(''); setLoading(true)
    try {
      const r = await api.phoneRegister(phone, smsCode, password, name.trim())
      if (r.access_token) await loginWithToken(r.access_token)
      else await phoneLogin(phone, smsCode)
      onClose()
    } catch (err) { setError(errMsg(err)) }
    finally { setLoading(false) }
  }

  const handleEmailReg = async (e) => {
    if (!agreed) return setError('请先阅读并同意《服务协议》与《隐私政策》')
    e.preventDefault()
    if (password.length < 6) return setError('密码至少 6 个字符')
    if (!name.trim()) return setError('请输入姓名')
    setError(''); setLoading(true)
    try { await register(email, password, name.trim()); onClose() }
    catch (err) { setError(errMsg(err)) }
    finally { setLoading(false) }
  }

  const handleForgot = async (e) => {
    e.preventDefault()
    if (!isValidPhone(phone)) return setError('请输入正确的手机号码')
    if (!smsCode || smsCode.length < 4) return setError('请输入验证码')
    if (password.length < 6) return setError('新密码至少 6 个字符')
    if (password !== confirmPw) return setError('两次密码不一致')
    setError(''); setLoading(true)
    try {
      await api.resetPassword(phone, smsCode, password)
      setMode('login'); setLoginTab('phone'); setPhoneLoginMode('password')
      setError('密码重置成功，请用新密码登录')
    } catch (err) { setError(errMsg(err)) }
    finally { setLoading(false) }
  }

  const isSuccess = error === '密码重置成功，请用新密码登录'
  const [fPhone, setFPhone] = useState(false)
  const [fEmail, setFEmail] = useState(false)
  const [fCode, setFCode] = useState(false)
  const [fName, setFName] = useState(false)
  const [fPw, setFPw] = useState(false)

  const Label = ({ children }) => (
    <label className="block text-[13px] font-medium mb-1.5 tracking-wide" style={{ color: '#4D4842' }}>
      {children}
    </label>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 backdrop-blur-md" style={{ background: 'rgba(21,21,21,0.45)' }} onClick={onClose} />

      <div className="relative w-full max-w-[420px] mx-4 rounded-2xl overflow-hidden animate-scale-in max-h-[90vh] overflow-y-auto"
        style={{ background: T.paper, boxShadow: '0 24px 64px rgba(0,0,0,0.12), 0 1px 0 rgba(0,0,0,0.04)' }}>
        
        <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${T.accent}, ${T.accentDark})` }} />

        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full transition-colors z-10"
          style={{ color: T.muted }}
          onMouseEnter={(e) => { e.currentTarget.style.background = T.hairlineLight; e.currentTarget.style.color = T.ink }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.muted }}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="px-8 pt-6 pb-8">
          {/* ── Header ── */}
          <div className="text-center mb-6">
            <img src={logoIcon} alt="" className="w-14 h-14 rounded-2xl mx-auto mb-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }} />
            <h2 className="text-xl font-bold tracking-tight" style={{ color: T.ink, fontFamily: "'Noto Serif SC', serif" }}>全景 OmniTuring</h2>
            <p className="text-[13px] mt-1" style={{ color: T.muted }}>
              {mode === 'login' && (pending2FA ? '该账号已开启两步验证' : '登录以继续使用')}
              {mode === 'register' && '创建您的账号'}
              {mode === 'forgot' && '重置您的密码'}
            </p>
          </div>

          {/* ── Error / Success ── */}
          {error && (
            <div className="mb-4 p-3 rounded-xl border text-[13px]"
              style={{ background: isSuccess ? '#f0fdf4' : '#fef2f2', borderColor: isSuccess ? '#bbf7d0' : '#fecaca', color: isSuccess ? '#16a34a' : '#dc2626' }}>
              {error}
            </div>
          )}

          {/* ═══════════ 2FA 续验（login 的第二步，不是新流程）═══════════ */}
          {mode === 'login' && pending2FA && (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <p className="text-[13px] leading-relaxed" style={{ color: T.muted }}>
                账号 <span style={{ color: T.ink, fontWeight: 500 }}>{pending2FA.identifier}</span>
                {' '}已开启两步验证，请输入验证器里的 6 位动态码，或一张备用码。
              </p>
              <div>
                <Label>验证码</Label>
                <input
                  type="text" inputMode="numeric" autoComplete="one-time-code" value={totp}
                  onChange={(e) => setTotp(e.target.value.trim())}
                  className={inputCls} style={inputStyle(fCode)}
                  placeholder="6 位动态码 / xxxx-xxxx 备用码" required
                  onFocus={() => setFCode(true)} onBlur={() => setFCode(false)} />
              </div>
              <div className="pt-1"><PrimaryButton loading={loading}>验证并登录</PrimaryButton></div>
              <button type="button" onClick={cancel2FA}
                className="text-[12px] transition-colors block" style={{ color: T.accent }}>
                返回重新登录
              </button>
            </form>
          )}

          {/* ═══════════ LOGIN ═══════════ */}
          {mode === 'login' && !pending2FA && (
            <>
              <TabBar tabs={[{ key: 'phone', label: '手机号' }, { key: 'email', label: '邮箱' }, { key: 'wechat', label: '微信' }]}
                active={loginTab} onChange={(k) => { setLoginTab(k); clearError() }} />

              {loginTab === 'phone' && (
                <>
                  {/* Phone login mode toggle */}
                  <div className="flex justify-end mb-3">
                    <button type="button" onClick={() => { setPhoneLoginMode(phoneLoginMode === 'password' ? 'sms' : 'password'); clearError() }}
                      className="text-[12px] transition-colors" style={{ color: T.accent }}
                      onMouseEnter={(e) => e.currentTarget.style.color = T.accentDark}
                      onMouseLeave={(e) => e.currentTarget.style.color = T.accent}>
                      {phoneLoginMode === 'password' ? '验证码登录' : '密码登录'}
                    </button>
                  </div>

                  {phoneLoginMode === 'password' ? (
                    <form onSubmit={handlePhonePasswordLogin} className="space-y-4">
                      <div>
                        <Label>手机号</Label>
                        <div className="flex items-stretch gap-2">
                          <span className="shrink-0 flex items-center justify-center px-4 rounded-xl border text-sm font-medium" style={{ borderColor: T.hairline, color: T.muted, background: T.paper, minWidth: '4rem' }}>
                            +86
                          </span>
                          <input
                            type="tel" value={phone}
                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                            className={inputCls} style={inputStyle(fPhone)}
                            placeholder="请输入手机号" maxLength={11}
                            onFocus={() => setFPhone(true)} onBlur={() => setFPhone(false)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>密码</Label>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                          className={inputCls} style={inputStyle(fPw)}
                          placeholder="请输入密码" required minLength={6}
                          onFocus={() => setFPw(true)} onBlur={() => setFPw(false)} />
                      </div>
                      <div className="pt-1"><PrimaryButton loading={loading}>登录</PrimaryButton></div>
                    </form>
                  ) : (
                    <form onSubmit={handlePhoneSmsLogin} className="space-y-4">
                      <div>
                        <Label>手机号</Label>
                        <div className="flex items-stretch gap-2">
                          <span className="shrink-0 flex items-center justify-center px-4 rounded-xl border text-sm font-medium" style={{ borderColor: T.hairline, color: T.muted, background: T.paper, minWidth: '4rem' }}>
                            +86
                          </span>
                          <input
                            type="tel" value={phone}
                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                            className={inputCls} style={inputStyle(fPhone)}
                            placeholder="请输入手机号" maxLength={11}
                            onFocus={() => setFPhone(true)} onBlur={() => setFPhone(false)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>验证码</Label>
                        <div className="flex gap-2">
                          <input
                            type="text" value={smsCode}
                            onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className={inputCls} style={inputStyle(fCode)}
                            placeholder="请输入验证码" maxLength={6}
                            onFocus={() => setFCode(true)} onBlur={() => setFCode(false)}
                          />
                          <SmsButton phone={phone} onError={setError} />
                        </div>
                      </div>
                      <div className="pt-1"><PrimaryButton loading={loading}>登录</PrimaryButton></div>
                    </form>
                  )}
                </>
              )}

              {loginTab === 'email' && (
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div><Label>邮箱</Label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      className={inputCls} style={inputStyle(fEmail)} placeholder="you@example.com"
                      onFocus={() => setFEmail(true)} onBlur={() => setFEmail(false)} />
                  </div>
                  <div><Label>密码</Label>
                    <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="请输入密码" required minLength={6} />
                  </div>
                  <div className="pt-1"><PrimaryButton loading={loading}>登录</PrimaryButton></div>
                </form>
              )}

              {loginTab === 'wechat' && (
                <div className="flex flex-col items-center py-6">
                  {wechatLoading && <div className="w-48 h-48 flex items-center justify-center"><div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: T.hairline, borderTopColor: T.accent }} /></div>}
                  {wechatError && (
                    <div className="w-48 h-48 flex flex-col items-center justify-center" style={{ color: T.muted }}>
                      <svg className="w-10 h-10 mb-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                      </svg>
                      <span className="text-sm">微信扫码暂未开放</span>
                    </div>
                  )}
                  {wechatQrUrl && !wechatError && (
                    <>
                      <div className="w-48 h-48 border rounded-2xl overflow-hidden mb-4" style={{ borderColor: T.hairline }}>
                        <img src={wechatQrUrl} alt="微信登录" className="w-full h-full object-contain" />
                      </div>
                      <p className="text-sm" style={{ color: T.muted }}>使用微信扫描二维码登录</p>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* ═══════════ REGISTER ═══════════ */}
          {mode === 'register' && (
            <>
              <TabBar tabs={[{ key: 'phone', label: '手机注册' }, { key: 'email', label: '邮箱注册' }]}
                active={regTab} onChange={(k) => { setRegTab(k); clearError() }} />

              {regTab === 'phone' ? (
                <form onSubmit={handlePhoneReg} className="space-y-4">
                  <div><Label>姓名</Label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                      className={inputCls} style={inputStyle(fName)} placeholder="请输入姓名"
                      onFocus={() => setFName(true)} onBlur={() => setFName(false)} />
                  </div>
                  <div>
                    <Label>手机号</Label>
                    <div className="flex items-stretch gap-2">
                      <span className="shrink-0 flex items-center justify-center px-4 rounded-xl border text-sm font-medium" style={{ borderColor: T.hairline, color: T.muted, background: T.paper, minWidth: '4rem' }}>
                        +86
                      </span>
                      <input
                        type="tel" value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        className={inputCls} style={inputStyle(fPhone)}
                        placeholder="请输入手机号" maxLength={11}
                        onFocus={() => setFPhone(true)} onBlur={() => setFPhone(false)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>验证码</Label>
                    <div className="flex gap-2">
                      <input
                        type="text" value={smsCode}
                        onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className={inputCls} style={inputStyle(fCode)}
                        placeholder="请输入验证码" maxLength={6}
                        onFocus={() => setFCode(true)} onBlur={() => setFCode(false)}
                      />
                      <SmsButton phone={phone} onError={setError} />
                    </div>
                  </div>
                  <div><Label>设置密码</Label>
                    <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="至少 6 个字符" required minLength={6} />
                  </div>
                  <label className="flex items-start gap-2 text-[12px] leading-relaxed cursor-pointer select-none" style={{ color: '#78716C' }}>
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                           className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ accentColor: '#C1751B' }} />
                    <span>我已阅读并同意
                      <Link to="/terms" target="_blank" className="underline" style={{ color: '#C1751B' }}>《服务协议》</Link>
                      与
                      <Link to="/privacy" target="_blank" className="underline" style={{ color: '#C1751B' }}>《隐私政策》</Link>
                    </span>
                  </label>
                  <div className="pt-1"><PrimaryButton loading={loading}>注册</PrimaryButton></div>
                </form>
              ) : (
                <form onSubmit={handleEmailReg} className="space-y-4">
                  <div><Label>姓名</Label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                      className={inputCls} style={inputStyle(fName)} placeholder="请输入姓名"
                      onFocus={() => setFName(true)} onBlur={() => setFName(false)} />
                  </div>
                  <div><Label>邮箱</Label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      className={inputCls} style={inputStyle(fEmail)} placeholder="you@example.com"
                      onFocus={() => setFEmail(true)} onBlur={() => setFEmail(false)} />
                  </div>
                  <div><Label>设置密码</Label>
                    <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="至少 6 个字符" required minLength={6} />
                  </div>
                  <label className="flex items-start gap-2 text-[12px] leading-relaxed cursor-pointer select-none" style={{ color: '#78716C' }}>
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                           className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ accentColor: '#C1751B' }} />
                    <span>我已阅读并同意
                      <Link to="/terms" target="_blank" className="underline" style={{ color: '#C1751B' }}>《服务协议》</Link>
                      与
                      <Link to="/privacy" target="_blank" className="underline" style={{ color: '#C1751B' }}>《隐私政策》</Link>
                    </span>
                  </label>
                  <div className="pt-1"><PrimaryButton loading={loading}>注册</PrimaryButton></div>
                </form>
              )}
            </>
          )}

          {/* ═══════════ FORGOT ═══════════ */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgot} className="space-y-4">
              <div>
                <Label>手机号</Label>
                <div className="flex items-stretch gap-2">
                  <span className="shrink-0 flex items-center justify-center px-4 rounded-xl border text-sm font-medium" style={{ borderColor: T.hairline, color: T.muted, background: T.paper, minWidth: '4rem' }}>
                    +86
                  </span>
                  <input
                    type="tel" value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    className={inputCls} style={inputStyle(fPhone)}
                    placeholder="请输入手机号" maxLength={11}
                    onFocus={() => setFPhone(true)} onBlur={() => setFPhone(false)}
                  />
                </div>
              </div>
              <div>
                <Label>验证码</Label>
                <div className="flex gap-2">
                  <input
                    type="text" value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className={inputCls} style={inputStyle(fCode)}
                    placeholder="请输入验证码" maxLength={6}
                    onFocus={() => setFCode(true)} onBlur={() => setFCode(false)}
                  />
                  <SmsButton phone={phone} onError={setError} />
                </div>
              </div>
              <div><Label>新密码</Label>
                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="至少 6 个字符" required minLength={6} />
              </div>
              <div><Label>确认密码</Label>
                <PasswordInput value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="再次输入新密码" required minLength={6} />
              </div>
              <div className="pt-1"><PrimaryButton loading={loading}>重置密码</PrimaryButton></div>
            </form>
          )}

          {/* ── Footer links ── */}
          <div className="mt-5 space-y-1.5 text-center">
            {mode === 'forgot' ? (
              <button onClick={() => switchMode('login')} className="text-[13px] font-medium" style={{ color: T.accent }}>返回登录</button>
            ) : mode === 'register' ? (
              <p className="text-[13px]" style={{ color: T.muted }}>
                已有账号？{' '}
                <button onClick={() => switchMode('login')} className="font-medium" style={{ color: T.accent }}>登录</button>
              </p>
            ) : (
              <>
                <p className="text-[13px]" style={{ color: T.muted }}>
                  没有账号？{' '}
                  <button onClick={() => switchMode('register')} className="font-medium" style={{ color: T.accent }}>立即注册</button>
                </p>
                {loginTab === 'phone' && phoneLoginMode === 'password' && (
                  <button onClick={() => switchMode('forgot')} className="text-[12px]" style={{ color: '#8E8880' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = T.muted}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#8E8880'}>忘记密码？</button>
                )}
              </>
            )}
          </div>

          {/* ── Brand watermark ── */}
          <div className="mt-6 pt-4 text-center" style={{ borderTop: `1px solid ${T.hairlineLight}` }}>
            <p className="text-[11px] tracking-wide" style={{ color: '#8E8880' }}>TANGENT MARK · 正切科技</p>
          </div>
        </div>
      </div>
    </div>
  )
}
